import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { success, error, confirm } from '../utils/alert';
import { Inbox, Trash2, Plus } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH');

export default function DebtUser() {
  const [debtors, setDebtors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/debtors');
      setDebtors(res.data);
    } catch { error('ໂຫຼດຂໍ້ມູນບໍ່ໄດ້'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/debtors', form);
      success('ເພີ່ມລູກໜີ້ແລ້ວ');
      setForm({ name: '', phone: '', note: '' });
      setShowAdd(false);
      load();
    } catch (err) {
      error(err.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    const res = await confirm(`ລົບ "${name}"?`, 'ຂໍ້ມູນຈະຖືກປິດການໃຊ້ງານ', 'ລົບ');
    if (!res.isConfirmed) return;
    try {
      await api.delete(`/debtors/${id}`);
      success('ລົບແລ້ວ');
      load();
    } catch { error('ລົບບໍ່ໄດ້'); }
  };

  const totalDebt = debtors.reduce((s, d) => s + (d.totalDebt || 0), 0);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="pt-2 flex items-center justify-between">
        <div>
          <h1 className="page-title">ລູກໜີ້</h1>
          <p className="text-muted text-xs">{debtors.length} ລາຍຊື່ • ຄ້າງຢູ່ {fmt(totalDebt)} KIP</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="btn-primary text-xs px-3 py-2 flex items-center gap-1">
          <Plus size={14} /> ເພີ່ມ
        </button>
      </div>

      {/* Summary */}
      <div className="card bg-gradient-to-r from-dark to-dark/80 border-0">
        <p className="text-cream/50 text-xs">ຍອດຄ້າງຮັບທັງໝົດ</p>
        <p className="text-sand text-2xl font-bold mt-0.5">{fmt(totalDebt)} KIP</p>
        <p className="text-cream/30 text-xs">{debtors.filter(d => (d.totalDebt || 0) > 0).length} ລາຍທີ່ຍັງຄ້າງ</p>
      </div>

      {loading ? (
        <div className="flex justify-center pt-8">
          <div className="w-7 h-7 border-2 border-dark border-t-transparent rounded-full animate-spin" />
        </div>
      ) : debtors.length === 0 ? (
        <div className="card text-center py-8">
          <div className="flex justify-center mb-2 text-muted/40"><Inbox size={36} /></div>
          <p className="text-muted text-sm">ຍັງບໍ່ມີລູກໜີ້</p>
        </div>
      ) : (
        <div className="space-y-2">
          {debtors.map((d, i) => (
            <div key={d._id} className="card flex items-center gap-3 animate-slide-up"
              style={{ animationDelay: `${i * 40}ms` }}>
              <Link to={`/debtors/${d._id}`} className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                  ${(d.totalDebt || 0) > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <span className="font-bold text-sm text-dark">{d.name[0]}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-dark text-sm truncate">{d.name}</p>
                  {d.phone && <p className="text-xs text-muted truncate">{d.phone}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-bold text-sm ${(d.totalDebt || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {fmt(d.totalDebt)} KIP
                  </p>
                  <p className="text-xs text-muted">{(d.totalDebt || 0) <= 0 ? 'ຊຳລະແລ້ວ' : 'ຄ້າງຢູ່'}</p>
                </div>
              </Link>
              <button onClick={() => handleDelete(d._id, d.name)}
                className="text-red-400 p-1.5 flex-shrink-0">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
          <div className="relative w-full max-w-lg bg-cream rounded-t-3xl p-5 pb-8 animate-slide-up">
            <div className="w-10 h-1 bg-muted/30 rounded-full mx-auto mb-4" />
            <h3 className="font-bold text-dark mb-4">ເພີ່ມລູກໜີ້ໃໝ່</h3>
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <label className="label">ຊື່ *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="ຊື່ລູກໜີ້" className="input-field" required />
              </div>
              <div>
                <label className="label">ເບີໂທ</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="0xx-xxx-xxxx" className="input-field" type="tel" />
              </div>
              <div>
                <label className="label">ໝາຍເຫດ</label>
                <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="ບັນທຶກ..." className="input-field" />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">ຍົກເລີກ</button>
                <button type="submit" disabled={saving} className="btn-primary flex items-center justify-center gap-2">
                  {saving ? <div className="w-4 h-4 border-2 border-cream border-t-transparent rounded-full animate-spin" /> : 'ເພີ່ມ'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
